import { LOCAL_ROLES, type LocalRole } from '@carsfinders/contracts';
import { useQuery } from '@tanstack/react-query';
import { useId, useState } from 'react';

import { t } from '../../../lib/i18n';
import { toast } from '../../../lib/toast';
import { apiErrorCode, fetchUserById } from '../api';
import { useSessionUser } from '../hooks/use-session-user';
import {
  draftOf,
  isRolesDraftDirty,
  normaliseRoles,
  unmanagedRoles,
  useUserRoles,
  withAdmin,
  withSuperadmin,
  type RolesDraft,
} from '../hooks/use-user-roles';
import { type User, type WireUserRoleJournalEntry } from '../types';
import { clientName } from '../utils';

/** How many journal rows the card shows under the form. */
const JOURNAL_ROWS = 5;

/** `17.10.2026` — the journal's «змінено» date. */
function shortDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('uk-UA', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(date);
}

function rolesText(roles: string[]): string {
  return roles.length > 0 ? roles.join(', ') : t('clients.roles.noRoles');
}

function saveErrorText(error: unknown): string {
  switch (apiErrorCode(error)) {
    case 'SELF_DEMOTION':
      return t('clients.roles.errors.selfDemotion');
    case 'CONFLICT':
      return t('clients.roles.errors.conflict');
    case 'NO_CHANGE':
      return t('clients.roles.errors.noChange');
    default:
      return t('clients.roles.errors.saveFailed');
  }
}

/**
 * «Ролі» — superadmin-only card on the client page («Верифікація» tab).
 *
 * Manages exactly `admin`, `superadmin` and one `local_*` role; every other
 * role on the user (`user`, `dealer`, …) is shown read-only and kept by the
 * API. Superadmin ⇒ admin is enforced as the boxes are ticked. A superadmin
 * editing themselves cannot drop their own admin/superadmin (the API refuses
 * with SELF_DEMOTION too). Each save writes one journal row; the latest five
 * are listed under the form.
 */
export function UserRolesCard({ user }: { user: User }) {
  const userId = user._id ?? '';
  const headingId = useId();
  const localId = useId();
  const selfHintId = useId();
  const { user: sessionUser } = useSessionUser();
  const { journalQuery, saveMutation } = useUserRoles(userId);

  const stored = normaliseRoles(user.role);
  const saved = draftOf(stored);
  const others = unmanagedRoles(stored);
  const isSelf = !!sessionUser?._id && sessionUser._id === userId;

  // `null` = untouched: the draft IS the stored roles, so a refetch after save
  // re-renders without clobbering anything.
  const [override, setOverride] = useState<RolesDraft | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const draft = override ?? saved;
  const dirty = isRolesDraftDirty(saved, draft);
  const saving = saveMutation.isPending;

  const edit = (next: RolesDraft) => {
    setSaveError(null);
    setOverride(next);
  };

  const save = async () => {
    if (!dirty || saving) return;
    setSaveError(null);
    try {
      await saveMutation.mutateAsync(draft);
      setOverride(null);
      toast.success(t('clients.roles.saved'));
    } catch (error) {
      setSaveError(saveErrorText(error));
    }
  };

  const checkboxClass =
    'h-4 w-4 accent-cf-brand focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cf-ink disabled:cursor-not-allowed';

  return (
    <section
      aria-labelledby={headingId}
      data-testid="user-roles-card"
      className="flex flex-col gap-[18px] rounded-cf-lg border border-cf-line bg-cf-surface p-4 leading-[normal] lg:px-6 lg:py-[22px]"
    >
      <h2 id={headingId} className="m-0 text-[18px] font-semibold tracking-[-0.02em]">
        {t('clients.roles.title')}
      </h2>

      <div className="flex flex-col gap-2.5">
        <label className="flex items-center gap-2.5 text-[14px] text-cf-ink">
          <input
            type="checkbox"
            data-testid="user-roles-admin"
            className={checkboxClass}
            checked={draft.admin}
            disabled={isSelf || saving}
            aria-describedby={isSelf ? selfHintId : undefined}
            onChange={(event) => edit(withAdmin(draft, event.target.checked))}
          />
          {t('clients.roles.admin')}
        </label>
        <label className="flex items-center gap-2.5 text-[14px] text-cf-ink">
          <input
            type="checkbox"
            data-testid="user-roles-superadmin"
            className={checkboxClass}
            checked={draft.superadmin}
            disabled={isSelf || saving}
            aria-describedby={isSelf ? selfHintId : undefined}
            onChange={(event) => edit(withSuperadmin(draft, event.target.checked))}
          />
          {t('clients.roles.superadmin')}
        </label>
        {isSelf && (
          <p
            id={selfHintId}
            data-testid="user-roles-self-hint"
            className="m-0 text-[12px] leading-[1.5] text-cf-ink-subtle"
          >
            {t('clients.roles.selfHint')}
          </p>
        )}
      </div>

      <div className="flex flex-col gap-2">
        <label htmlFor={localId} className="text-[13px] text-cf-ink-muted">
          {t('clients.roles.localLabel')}
        </label>
        <select
          id={localId}
          data-testid="user-roles-local"
          value={draft.local ?? ''}
          disabled={saving}
          onChange={(event) =>
            edit({ ...draft, local: (event.target.value || null) as LocalRole | null })
          }
          className="h-10 rounded-cf-md border border-cf-line bg-cf-surface px-3 text-[14px] text-cf-ink focus-visible:outline-2 focus-visible:outline-cf-ink"
        >
          <option value="">{t('clients.roles.local.none')}</option>
          {LOCAL_ROLES.map((role) => (
            <option key={role} value={role}>
              {t(`clients.roles.local.${role}`)}
            </option>
          ))}
        </select>
      </div>

      <div className="flex flex-col gap-1.5">
        <span className="text-[13px] text-cf-ink-muted">
          {t('clients.roles.otherLabel')}{' '}
          <span className="text-[12px] text-cf-ink-subtle">({t('clients.roles.otherHint')})</span>
        </span>
        <ul data-testid="user-roles-other" className="m-0 flex list-none flex-wrap gap-1.5 p-0">
          {others.length === 0 ? (
            <li className="text-[13px] text-cf-ink-subtle">{t('clients.roles.noRoles')}</li>
          ) : (
            others.map((role) => (
              <li
                key={role}
                className="rounded-cf-md bg-cf-fill px-2 py-0.5 font-mono text-[12px] text-cf-ink"
              >
                {role}
              </li>
            ))
          )}
        </ul>
      </div>

      {saveError && (
        <p
          role="alert"
          data-testid="user-roles-save-error"
          className="m-0 rounded-cf-md bg-cf-danger-tint px-3 py-2.5 text-[13px] leading-[1.5] text-cf-danger"
        >
          {saveError}
        </p>
      )}

      <div className="flex justify-end border-t border-cf-line-soft pt-4">
        <button
          type="button"
          data-testid="user-roles-save"
          onClick={() => void save()}
          disabled={!dirty || saving}
          className="h-10 shrink-0 rounded-cf-md bg-cf-brand px-4 text-[14px] font-semibold text-white hover:bg-cf-brand-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cf-ink disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-cf-brand"
        >
          {saving ? t('clients.roles.saving') : t('clients.roles.save')}
        </button>
      </div>

      {(journalQuery.data?.length ?? 0) > 0 && (
        <div className="flex flex-col gap-1.5">
          <span className="font-mono text-[10px] tracking-[0.12em] text-cf-ink-subtle uppercase">
            {t('clients.roles.journalTitle')}
          </span>
          <ul data-testid="user-roles-journal" className="m-0 flex list-none flex-col gap-1 p-0">
            {journalQuery.data?.slice(0, JOURNAL_ROWS).map((entry, index) => (
              <JournalRow key={`${entry.changedAt}-${index}`} entry={entry} />
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

/** «змінено 17.10.2026 · Андрій: user → user, admin». */
function JournalRow({ entry }: { entry: WireUserRoleJournalEntry }) {
  // Same key the client page uses for a user, so an admin already loaded is free.
  const whoQuery = useQuery({
    queryKey: ['user', entry.changedBy],
    queryFn: () => fetchUserById(entry.changedBy),
    enabled: entry.changedBy.length > 0,
    retry: false,
  });
  const who = whoQuery.data ? clientName(whoQuery.data) : '';
  const params = {
    date: shortDate(entry.changedAt),
    before: rolesText(entry.previous),
    after: rolesText(entry.next),
  };
  return (
    <li className="font-mono text-[12px] leading-[1.5] break-words text-cf-ink-subtle">
      {who
        ? t('clients.roles.changed', { ...params, who })
        : t('clients.roles.changedNoWho', params)}
    </li>
  );
}
